import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { sendOnboardingData } from '../utils/webhookService';

const OnboardingForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    businessType: '',
    interests: [],
    phoneCallsPerDay: '',
    averageCallDuration: '',
    gdprConsent: false,
    marketingConsent: false
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessTypes = [
    'Einzelhandel',
    'E-Commerce', 
    'Gastronomie',
    'Dienstleistung',
    'Beratung/Coaching',
    'Gesundheitswesen',
    'Handwerk',
    'Sonstiges'
  ];

  const interestOptions = [
    'Automatisierte Terminbuchung',
    'Kundenservice-Chatbot',
    'Lead-Qualifizierung',
    'Bestellannahme',
    'FAQ-Automatisierung',
    'Kundenfeedback-System',
    'Verkaufsunterstützung',
    'Nachfass-Automatisierung'
  ];

  const callVolumeOptions = [
    '1-5 Anrufe pro Tag',
    '6-15 Anrufe pro Tag',
    '16-30 Anrufe pro Tag',
    '31-50 Anrufe pro Tag',
    'Mehr als 50 Anrufe pro Tag'
  ];

  const callDurationOptions = [
    '1-3 Minuten',
    '4-8 Minuten',
    '9-15 Minuten',
    '16-30 Minuten',
    'Mehr als 30 Minuten'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleInterestToggle = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Prepare data for webhook
      const webhookData = {
        personalInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        companyInfo: {
          company: formData.company,
          businessType: formData.businessType
        },
        communicationVolume: {
          phoneCallsPerDay: formData.phoneCallsPerDay,
          averageCallDuration: formData.averageCallDuration
        },
        interests: formData.interests,
        consent: {
          gdprConsent: formData.gdprConsent,
          marketingConsent: formData.marketingConsent
        },
        submissionDate: new Date().toISOString(),
        source: 'onboarding-form'
      };

      // Send to webhook
      const webhookResult = await sendOnboardingData(webhookData);
      
      if (webhookResult.success) {
        console.log('Onboarding data sent to webhook successfully');
      } else {
        console.error('Failed to send to webhook:', webhookResult.error);
      }

      // Store form data locally as backup
      localStorage.setItem('onboardingData', JSON.stringify(formData));
      
      // Redirect to thank you page
      navigate('/danke');
    } catch (error) {
      console.error('Submission error:', error);
      alert('Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.email;
      case 2:
        return formData.company && formData.phone;
      case 3:
        return formData.businessType;
      case 4:
        return formData.phoneCallsPerDay && formData.averageCallDuration;
      case 5:
        return formData.gdprConsent;
      default:
        return false;
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Schritt {currentStep} von 5</span>
                <span className="text-sm text-gray-600">{Math.round((currentStep / 5) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-azure-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-black mb-4">Persönliche Angaben</h2>
                  <p className="text-gray-600">Lassen Sie uns Sie kennenlernen</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="Max"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="Mustermann"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail-Adresse *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    placeholder="ihre.email@beispiel.de"
                    required
                  />
                </div>
              </motion.div>
            )}

            {/* Step 2: Company Information */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-black mb-4">Unternehmensangaben</h2>
                  <p className="text-gray-600">Erzählen Sie uns von Ihrem Unternehmen</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unternehmen *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    placeholder="Ihr Unternehmen"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                    placeholder="+49 123 456789"
                    required
                  />
                </div>
              </motion.div>
            )}

            {/* Step 3: Business Type */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-black mb-4">Ihre Branche</h2>
                  <p className="text-gray-600">Wählen Sie Ihre Branche</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Branche *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {businessTypes.map((type) => (
                      <label
                        key={type}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessType === type
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessType"
                          value={type}
                          checked={formData.businessType === type}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="font-medium">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Communication Volume */}
            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-black mb-4">Kommunikationsvolumen</h2>
                  <p className="text-gray-600">Wie viele Telefonate führen Sie?</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Anzahl Telefonate pro Tag *
                  </label>
                  <div className="space-y-3">
                    {callVolumeOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          formData.phoneCallsPerDay === option
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="phoneCallsPerDay"
                          value={option}
                          checked={formData.phoneCallsPerDay === option}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Durchschnittliche Gesprächsdauer *
                  </label>
                  <div className="space-y-3">
                    {callDurationOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                          formData.averageCallDuration === option
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="averageCallDuration"
                          value={option}
                          checked={formData.averageCallDuration === option}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="font-medium">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Interessensgebiete (optional, mehrfach auswählbar)
                  </label>
                  <div className="space-y-3">
                    {interestOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.interests.includes(option)
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(option)}
                          onChange={() => handleInterestToggle(option)}
                          className="mr-3 h-4 w-4 text-azure-600 focus:ring-azure-500 border-gray-300 rounded"
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 5: GDPR Consent */}
            {currentStep === 5 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-black mb-4">Datenschutz & Einverständnis</h2>
                  <p className="text-gray-600">Ihre Zustimmung nach DSGVO</p>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-black mb-3">Datenverarbeitung</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Wir verarbeiten Ihre personenbezogenen Daten zur Bereitstellung unserer KI-Beratungsleistungen. 
                      Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                    </p>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="gdprConsent"
                        checked={formData.gdprConsent}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-azure-600 focus:ring-azure-500 border-gray-300 rounded"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        Ich stimme der Verarbeitung meiner personenbezogenen Daten gemäß der Datenschutzerklärung zu. *
                      </span>
                    </label>
                  </div>

                  <div className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-black mb-3">Marketing-Kommunikation (optional)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Möchten Sie über neue KI-Lösungen und Updates informiert werden?
                    </p>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="marketingConsent"
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-azure-600 focus:ring-azure-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        Ja, ich möchte über KI-Neuigkeiten und Angebote per E-Mail informiert werden. 
                        Diese Einwilligung kann jederzeit widerrufen werden.
                      </span>
                    </label>
                  </div>

                  <div className="text-xs text-gray-500 bg-blue-50 p-4 rounded-lg">
                    <p className="mb-2">
                      <strong>Ihre Rechte:</strong> Sie haben das Recht auf Auskunft, Berichtigung, 
                      Löschung und Einschränkung der Verarbeitung Ihrer Daten.
                    </p>
                    <p>
                      <strong>Kontakt:</strong> Bei Fragen zum Datenschutz wenden Sie sich an:{' '}
                      <a href="mailto:mike.trepte@ki-kundenkommunikation.de" className="text-azure-600">
                        mike.trepte@ki-kundenkommunikation.de
                      </a>
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Zurück
              </button>
              
              {currentStep < 5 ? (
                <button
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className="px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Weiter
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid() || isSubmitting}
                  className="px-6 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Wird gesendet...
                    </span>
                  ) : (
                    'Absenden'
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Nach dem Absenden werden Sie zur Danke-Seite weitergeleitet, wo Sie Ihr Dokument herunterladen können.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingForm;